import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLicense } from '../context/LicenseContext';
import { useTheme } from '../context/ThemeContext';
import { useSite } from '../context/SiteContext';
import { useAlarmSocket } from '../context/AlarmSocket';
import { useAudit } from '../context/AuditContext';

// NAV item type — external:true items open in a new tab at /{slug} directly
export const NAV: Array<{ label: string; items: Array<{ name: string; slug: string; external?: boolean }> }> = [
  { label: 'OPERATIONS', items: [
    { name: 'SOC Dashboard',       slug: 'soc' },
    { name: 'Jobs',                slug: 'jobs' },
    { name: 'Guard Tours',         slug: 'tours' },
    { name: 'Car Park',            slug: 'carpark' },
  ]},
  { label: 'ACCESS & FACILITIES', items: [
    { name: 'Access Control',      slug: 'access' },
    { name: 'Bookings',            slug: 'bookings' },
    { name: 'Public Booking Portal', slug: 'book', external: true },
    { name: 'Assets',              slug: 'assets' },
  ]},
  { label: 'INTELLIGENCE', items: [
    { name: 'Video',               slug: 'video' },
    { name: 'Smart Vision',        slug: 'vision' },
    { name: 'Executive Dashboard', slug: 'executive' },
    { name: 'Lemai Face',          slug: 'face' },
    { name: 'Reports',             slug: 'reports' },
  ]},
  { label: 'ADMIN', items: [
    { name: 'Site Programming',    slug: 'sites' },
    { name: 'User Management',     slug: 'admin/users' },
    { name: 'Settings',            slug: 'settings' },
  ]},
];

// ─── SVG Nav Icons ────────────────────────────────────────────────────────────
function NavIcon({ slug, size = 15, color = 'currentColor' }: { slug: string; size?: number; color?: string }) {
  const p: Record<string, React.ReactNode> = {
    soc:         <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
    video:       <><rect x="2" y="7" width="15" height="10" rx="1"/><polygon points="23 7 17 12 23 17 23 7"/></>,
    access:      <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    jobs:        <><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/></>,
    tours:       <><circle cx="12" cy="5" r="2"/><path d="M12 7v9"/><path d="M8 11l4 1 4-1"/><path d="M9 16l3 4 3-4"/></>,
    vision:      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    executive:   <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    face:        <><circle cx="12" cy="8" r="4"/><path d="M3 21v-1a9 9 0 0 1 18 0v1"/></>,
    reports:     <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 17V13M12 17V9M16 17V11"/></>,
    assets:      <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></>,
    carpark:     <><rect x="2" y="2" width="20" height="20" rx="3"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/></>,
    bookings:    <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/></>,
    book:        <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    integrations:<><circle cx="5" cy="12" r="3"/><circle cx="19" cy="5" r="3"/><circle cx="19" cy="19" r="3"/><line x1="8" y1="12" x2="16" y2="6.5"/><line x1="8" y1="12" x2="16" y2="17.5"/></>,
    sites:       <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/><path d="M12 7v2"/></>,
    'admin/users':<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    settings:    <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
    'admin/orgs': <><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 9h18M9 21V9"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {p[slug] ?? <circle cx="12" cy="12" r="10"/>}
    </svg>
  );
}

function isTopLevelSlug(slug: string) {
  return slug.startsWith('admin') || slug === 'settings';
}

// ─── Breadcrumbs ─────────────────────────────────────────────────────────────

const BREADCRUMB_MAP: Record<string, { name: string; group?: string }> = {
  '/dashboard':    { name: 'Dashboard' },
  '/m/soc':        { name: 'SOC Dashboard',       group: 'Operations' },
  '/m/jobs':       { name: 'Jobs',                group: 'Operations' },
  '/m/tours':      { name: 'Guard Tours',         group: 'Operations' },
  '/m/carpark':    { name: 'Car Park',            group: 'Operations' },
  '/m/access':     { name: 'Access Control',      group: 'Access & Facilities' },
  '/m/bookings':   { name: 'Bookings',            group: 'Access & Facilities' },
  '/m/assets':     { name: 'Assets',              group: 'Access & Facilities' },
  '/m/video':      { name: 'Video',               group: 'Intelligence' },
  '/m/vision':     { name: 'Smart Vision',        group: 'Intelligence' },
  '/m/executive':  { name: 'Executive Dashboard', group: 'Intelligence' },
  '/m/face':       { name: 'Lemai Face',          group: 'Intelligence' },
  '/m/reports':    { name: 'Reports',             group: 'Intelligence' },
  '/settings':     { name: 'Settings',            group: 'Admin' },
  '/admin/users':  { name: 'User Management',     group: 'Admin' },
  '/admin/orgs':   { name: 'Org Management',      group: 'Admin' },
  '/workspace':    { name: 'Workspace' },
};

function Breadcrumbs() {
  const location = useLocation();
  const crumb = BREADCRUMB_MAP[location.pathname];
  if (!crumb || location.pathname === '/dashboard') return null;
  const sep = <span style={{ color:'#2a2a2a', margin:'0 4px' }}>›</span>;
  return (
    <div style={{ display:'flex', alignItems:'center', padding:'4px 20px 0', fontSize:'10px', color:'#333', flexWrap:'wrap' }}>
      <NavLink to="/dashboard" style={{ color:'#333', textDecoration:'none', fontWeight:500 }}
        onMouseEnter={e => (e.currentTarget as HTMLElement).style.color='#555'}
        onMouseLeave={e => (e.currentTarget as HTMLElement).style.color='#333'}>
        Home
      </NavLink>
      {crumb.group && <>{sep}<span style={{ color:'#2a2a2a' }}>{crumb.group}</span></>}
      {sep}
      <span style={{ color:'#555', fontWeight:500 }}>{crumb.name}</span>
    </div>
  );
}

// ─── Notification Centre ──────────────────────────────────────────────────────

interface Notif {
  id: string;
  type: 'alarm' | 'job' | 'loneworker' | 'intercom' | 'escalation' | 'system';
  title: string;
  body: string;
  time: string;
  read: boolean;
  link?: string;
}

const SEED_NOTIFS: Notif[] = [
  { id:'N001', type:'alarm',      title:'Critical Alarm — Duress Button',          body:'Town Hall Control Room — SOC response required',               time:'08:41', read:false, link:'/m/soc' },
  { id:'N002', type:'job',        title:'Job JOB-20260410-0002 — Supervisor Review',body:'Break In / Intrusion at Civic Centre pending your sign-off',   time:'09:03', read:false, link:'/m/jobs' },
  { id:'N003', type:'loneworker', title:'Lone Worker Overdue',                      body:'AQ-POOL-GUARD check-in overdue by 13 minutes',                 time:'10:17', read:false, link:'/m/incidents' },
  { id:'N004', type:'intercom',   title:'Missed Intercom Call',                     body:'AQ-MAIN — Aquatic Centre Main Entry unanswered',               time:'09:58', read:true,  link:'/m/access' },
  { id:'N005', type:'escalation', title:'Alarm Escalated — No ACK after 5 min',    body:'Door Forced Open, Civic Centre — escalated to supervisor',     time:'07:22', read:true,  link:'/m/soc' },
  { id:'N006', type:'system',     title:'Inner Range Integriti Disconnected',       body:'Alarm panel integration offline — check Settings',             time:'06:45', read:true,  link:'/settings' },
];

const NOTIF_ICON: Record<Notif['type'], string> = {
  alarm:'🚨', job:'📋', loneworker:'👷', intercom:'📞', escalation:'⬆️', system:'⚙️',
};
const NOTIF_COLOR: Record<Notif['type'], string> = {
  alarm:'#ef4444', job:'#f59e0b', loneworker:'#f59e0b', intercom:'#3b82f6', escalation:'#a855f7', system:'#555555',
};

function NotifPanel({ notifs, onRead, onClose }: { notifs: Notif[]; onRead: (id: string) => void; onClose: () => void }) {
  const unread = notifs.filter(n => !n.read).length;
  const navigate = useNavigate();
  return (
    <div style={{ position:'fixed', top:'52px', right:'12px', width:'340px', background:'#111', border:'1px solid #333', borderRadius:'14px', zIndex:100, boxShadow:'0 20px 60px rgba(0,0,0,0.5)', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', gap:'10px' }}>
        <div style={{ fontWeight:700, fontSize:'14px', color:'white', flex:1 }}>Notifications</div>
        {unread > 0 && <div style={{ fontSize:'11px', color:'#3b82f6', fontWeight:600 }}>{unread} unread</div>}
        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'6px', color:'#555555', width:'26px', height:'26px', fontSize:'14px', cursor:'pointer' }}>✕</button>
      </div>
      <div style={{ maxHeight:'380px', overflowY:'auto' }}>
        {notifs.map(n => (
          <div key={n.id} onClick={() => { onRead(n.id); if (n.link) { navigate(n.link); onClose(); } }} style={{ display:'flex', gap:'10px', padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.05)', background: n.read ? 'transparent' : 'rgba(59,130,246,0.04)', cursor:'pointer' }}>
            <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:`${NOTIF_COLOR[n.type]}18`, border:`1px solid ${NOTIF_COLOR[n.type]}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', flexShrink:0, marginTop:'2px' }}>{NOTIF_ICON[n.type]}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'12px', fontWeight: n.read ? 500 : 700, color: n.read ? '#94a3b8' : 'white', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.title}</div>
              <div style={{ fontSize:'11px', color:'#555555', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.body}</div>
            </div>
            <div style={{ fontSize:'10px', color:'#222222', flexShrink:0, marginTop:'2px' }}>{n.time}</div>
          </div>
        ))}
      </div>
      {notifs.length === 0 && <div style={{ padding:'32px', textAlign:'center', color:'#222222', fontSize:'13px' }}>All caught up ✓</div>}
    </div>
  );
}

// ─── Global Search ────────────────────────────────────────────────────────────

const SEARCH_INDEX = [
  { label:'SOC Dashboard',       slug:'/m/soc',         navSlug:'soc',          desc:'Alarm monitoring, live feed, site map, suppression' },
  { label:'Video',               slug:'/m/video',       navSlug:'video',        desc:'Camera grid, views, camera map' },
  { label:'Access Control',      slug:'/m/access',      navSlug:'access',       desc:'Doors, readers, access events, intercom, site map' },
  { label:'Jobs',                slug:'/m/jobs',        navSlug:'jobs',         desc:'Job management, shift handover, work orders' },
  { label:'Guard Tours',         slug:'/m/tours',       navSlug:'tours',        desc:'Patrol scheduling, checkpoint scanning, compliance tracking' },
  { label:'Smart Vision',        slug:'/m/vision',      navSlug:'vision',       desc:'AI video analytics, heatmap, people counting, site map' },
  { label:'Executive Dashboard', slug:'/m/executive',   navSlug:'executive',    desc:'KPIs, site risk, weekly trends' },
  { label:'Lemai Face',          slug:'/m/face',        navSlug:'face',         desc:'Facial recognition, watchlist, access control' },
  { label:'Reports',             slug:'/m/reports',     navSlug:'reports',      desc:'Generate and export reports, PDF' },
  { label:'Assets',              slug:'/m/assets',      navSlug:'assets',       desc:'Asset registry, lifecycle, floor plans' },
  { label:'Bookings',            slug:'/m/bookings',    navSlug:'bookings',     desc:'Space bookings, access provisioning, kiosk' },
  { label:'Car Park',            slug:'/m/carpark',     navSlug:'carpark',      desc:'Parking management, bookings, vehicle AI' },
  { label:'Integrations',        slug:'/m/integrations',navSlug:'integrations', desc:'VMS, access control, alarm panel connections' },
  { label:'Settings',            slug:'/settings',      navSlug:'settings',     desc:'Cameras, integrations, SOP, alarm notifications, branding' },
];

const SEV_DOT: Record<string,string> = { critical:'#ef4444', high:'#f59e0b', medium:'#3b82f6', low:'#22c55e' };

function SearchModal({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [liveAlarms, setLiveAlarms] = useState<any[]>([]);
  const [liveJobs, setLiveJobs] = useState<any[]>([]);
  const navigate = useNavigate();

  // Fetch live data for search
  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    const hdr = { Authorization: `Bearer ${tok}` };
    Promise.all([
      fetch('/api/armorip/alarms', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/jobs', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([aRes, jRes]) => {
      setLiveAlarms((aRes?.alarms || []).slice(0, 50));
      setLiveJobs((jRes || []).slice(0, 50));
    });
  }, []);

  const ql = q.trim().toLowerCase();
  const moduleResults = ql.length < 1 ? SEARCH_INDEX : SEARCH_INDEX.filter(item =>
    item.label.toLowerCase().includes(ql) || item.desc.toLowerCase().includes(ql)
  );
  const alarmResults = ql.length >= 2 ? liveAlarms.filter(a =>
    (a.type||'').toLowerCase().includes(ql) ||
    (a.site||'').toLowerCase().includes(ql) ||
    (a.location||'').toLowerCase().includes(ql)
  ).slice(0, 4) : [];
  const jobResults = ql.length >= 2 ? liveJobs.filter(j =>
    (j.number||j.id||'').toLowerCase().includes(ql) ||
    (j.type||'').toLowerCase().includes(ql) ||
    (j.site||'').toLowerCase().includes(ql) ||
    (j.description||'').toLowerCase().includes(ql)
  ).slice(0, 4) : [];

  function go(slug: string) { navigate(slug); onClose(); }

  const rowStyle: React.CSSProperties = { display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.04)' };
  const hover = (e: React.MouseEvent) => (e.currentTarget as HTMLElement).style.background = 'rgba(59,130,246,0.08)';
  const unhover = (e: React.MouseEvent) => (e.currentTarget as HTMLElement).style.background = 'transparent';

  const totalResults = moduleResults.length + alarmResults.length + jobResults.length;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:500, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:'80px' }} onClick={onClose}>
      <div style={{ background:'#111111', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'16px', width:'100%', maxWidth:'540px', overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'14px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter' && moduleResults.length === 1) go(moduleResults[0].slug); }} placeholder="Search modules, alarms, jobs…" style={{ flex:1, background:'none', border:'none', outline:'none', color:'white', fontSize:'15px', fontFamily:'inherit' }} />
          <kbd style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', padding:'2px 7px', fontSize:'11px', color:'#555555' }}>ESC</kbd>
        </div>
        <div style={{ maxHeight:'420px', overflowY:'auto' }}>
          {/* Module results */}
          {moduleResults.length > 0 && (
            <>
              {ql.length >= 2 && <div style={{ padding:'8px 16px 4px', fontSize:'9px', fontWeight:700, color:'#222222', letterSpacing:'1px' }}>MODULES</div>}
              {moduleResults.map(item => (
                <div key={item.slug} onClick={() => go(item.slug)} style={rowStyle} onMouseEnter={hover} onMouseLeave={unhover}>
                  <div style={{ width:'28px', height:'28px', borderRadius:'7px', background:'rgba(59,130,246,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <NavIcon slug={item.navSlug} size={14} color="#3b82f6" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', fontWeight:600, color:'white' }}>{item.label}</div>
                    <div style={{ fontSize:'11px', color:'#555555', marginTop:'1px' }}>{item.desc}</div>
                  </div>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#222222" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              ))}
            </>
          )}
          {/* Live alarm results */}
          {alarmResults.length > 0 && (
            <>
              <div style={{ padding:'8px 16px 4px', fontSize:'9px', fontWeight:700, color:'#222222', letterSpacing:'1px' }}>LIVE ALARMS</div>
              {alarmResults.map((a, i) => (
                <div key={i} onClick={() => go('/m/soc')} style={rowStyle} onMouseEnter={hover} onMouseLeave={unhover}>
                  <div style={{ width:'7px', height:'7px', borderRadius:'50%', background:SEV_DOT[a.severity]||'#555555', flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', fontWeight:600, color:'white' }}>{a.type}</div>
                    <div style={{ fontSize:'11px', color:'#555555' }}>{a.site} · {a.location}</div>
                  </div>
                  <div style={{ fontSize:'10px', fontWeight:700, color:SEV_DOT[a.severity]||'#555555', textTransform:'uppercase' }}>{a.severity}</div>
                </div>
              ))}
            </>
          )}
          {/* Live job results */}
          {jobResults.length > 0 && (
            <>
              <div style={{ padding:'8px 16px 4px', fontSize:'9px', fontWeight:700, color:'#222222', letterSpacing:'1px' }}>JOBS</div>
              {jobResults.map((j, i) => (
                <div key={i} onClick={() => go('/m/jobs')} style={rowStyle} onMouseEnter={hover} onMouseLeave={unhover}>
                  <div style={{ width:'28px', height:'28px', borderRadius:'7px', background:'rgba(245,158,11,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <NavIcon slug="jobs" size={14} color="#f59e0b" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', fontWeight:600, color:'white', fontFamily:'monospace' }}>{j.number || j.id}</div>
                    <div style={{ fontSize:'11px', color:'#555555' }}>{j.type} · {j.site}</div>
                  </div>
                  <div style={{ fontSize:'10px', color:'#555555' }}>{j.status}</div>
                </div>
              ))}
            </>
          )}
          {totalResults === 0 && ql.length >= 1 && <div style={{ padding:'32px', textAlign:'center', color:'#222222', fontSize:'13px' }}>No results for "{q}"</div>}
          {ql.length === 0 && (
            <div style={{ padding:'14px 16px', fontSize:'11px', color:'#222222' }}>
              Type to search modules, live alarms, and jobs · <kbd style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', padding:'1px 5px', fontSize:'10px' }}>/</kbd> to open
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Org Switcher (super admin only) ─────────────────────────────────────────

interface OrgOption { id: number; name: string; }

function OrgSwitcherBanner() {
  const { isSuperAdmin, viewingOrg, switchOrg, resetOrg } = useLicense();
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = localStorage.getItem('lemai_token');
    fetch('/api/admin/orgs', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setOrgs((d.orgs || []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => {});
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  return (
    <div style={{ background: viewingOrg ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.06)', borderBottom: '1px solid ' + (viewingOrg ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.15)'), padding:'6px 16px', display:'flex', alignItems:'center', gap:'10px', fontSize:'12px', flexWrap:'wrap' }}>
      <span style={{ color: viewingOrg ? '#f59e0b' : '#3b82f6', fontWeight:600 }}>
        {viewingOrg ? `👁 Viewing: ${viewingOrg.org_name}` : '⚡ Super Admin — My Platform View'}
      </span>
      <span style={{ color:'#222222', flex:1 }}>
        {viewingOrg ? `${viewingOrg.licensed_modules.length} modules licensed` : 'You see all modules. Switch org to preview their view.'}
      </span>
      <div style={{ position:'relative' }}>
        <button onClick={() => setOpen(o => !o)} style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'6px', color:'white', padding:'4px 10px', fontSize:'11px', cursor:'pointer', fontWeight:600 }}>
          Switch Org ▾
        </button>
        {open && (
          <div style={{ position:'absolute', top:'100%', right:0, marginTop:'4px', background:'#111111', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'10px', minWidth:'200px', zIndex:200, boxShadow:'0 10px 40px rgba(0,0,0,0.5)', overflow:'hidden' }}>
            <div onClick={() => { resetOrg(); setOpen(false); }} style={{ padding:'10px 14px', cursor:'pointer', fontSize:'12px', color:'#3b82f6', borderBottom:'1px solid #222', fontWeight:600 }}>
              My Platform View (all modules)
            </div>
            {orgs.map(o => (
              <div key={o.id} onClick={() => { switchOrg(o.id); setOpen(false); }} style={{ padding:'10px 14px', cursor:'pointer', fontSize:'12px', color: viewingOrg?.org_id === o.id ? '#f59e0b' : '#94a3b8', borderBottom:'1px solid rgba(255,255,255,0.04)', fontWeight: viewingOrg?.org_id === o.id ? 600 : 400 }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background='rgba(255,255,255,0.04)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background='transparent'}>
                {o.name} {viewingOrg?.org_id === o.id ? '✓' : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Site Switcher ────────────────────────────────────────────────────────────

function SiteSwitcher() {
  const { sites, activeSiteId, activeSite, setActiveSiteId } = useSite();
  const [open, setOpen] = useState(false);
  const totalAlarms = sites.reduce((n, s) => n + s.alarmCount, 0);

  const statusColor = (st: string) =>
    st === 'online' ? '#22c55e' : st === 'degraded' ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ margin:'6px 10px 14px', position:'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', gap:'8px',
          padding:'4px 8px', borderRadius:0, border:'1px solid #1a1a1a',
          background:'#111', cursor:'pointer', textAlign:'left' as 'left',
        }}
      >
        <span style={{ fontSize:'14px' }}>🏢</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'10px', fontWeight:600, color:'#888', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {activeSite ? activeSite.name : 'All Sites'}
          </div>
          <div style={{ fontSize:'10px', color:'#555555' }}>
            {activeSite
              ? <span style={{ color:statusColor(activeSite.status) }}>{activeSite.status}</span>
              : `${totalAlarms} active alarm${totalAlarms !== 1 ? 's' : ''}`}
          </div>
        </div>
        <span style={{ color:'#555555', fontSize:'10px' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, marginTop:'4px',
          background:'#111', border:'1px solid #333',
          borderRadius:0, zIndex:300, overflow:'hidden',
        }}>
          <div
            onClick={() => { setActiveSiteId('all'); setOpen(false); }}
            style={{
              padding:'8px 12px', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'8px',
              borderBottom:'1px solid #222',
              background: activeSiteId === 'all' ? 'rgba(59,130,246,0.12)' : 'transparent',
              color: activeSiteId === 'all' ? '#93c5fd' : '#94a3b8', fontWeight: activeSiteId === 'all' ? 600 : 400,
            }}
          >
            <span>🌐</span> All Sites
            {totalAlarms > 0 && <span style={{ marginLeft:'auto', fontSize:'10px', color:'#ef4444', fontWeight:700 }}>{totalAlarms} alarms</span>}
          </div>
          {sites.map(site => (
            <div
              key={site.id}
              onClick={() => { setActiveSiteId(site.id); setOpen(false); }}
              style={{
                padding:'8px 12px', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'8px',
                borderBottom:'1px solid rgba(255,255,255,0.04)',
                background: activeSiteId === site.id ? 'rgba(59,130,246,0.1)' : 'transparent',
                color: activeSiteId === site.id ? '#93c5fd' : '#94a3b8',
              }}
              onMouseEnter={e => { if (activeSiteId !== site.id) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={e => { if (activeSiteId !== site.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width:'7px', height:'7px', borderRadius:'50%', background:statusColor(site.status), flexShrink:0, display:'inline-block' }} />
              <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{site.name}</span>
              {site.alarmCount > 0 && <span style={{ fontSize:'10px', color:'#ef4444', fontWeight:700 }}>{site.alarmCount}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout } = useAuth();
  const { hasModule, isSuperAdmin, viewingOrg } = useLicense();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { alarms: _sidebarAlarms } = useAlarmSocket();
  const _socActiveCount = _sidebarAlarms.filter(a => !a.acknowledged && !a.closedAt).length;

  return (
    <>
      {open && <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:40,backdropFilter:'blur(2px)'}} />}
      <div style={{position:'fixed',top:0,left:0,bottom:0,width:'260px',background:'#111111',borderRight:'1px solid #222',display:'flex',flexDirection:'column',zIndex:50,transform:open?'translateX(0)':'translateX(-100%)',transition:'transform 0.28s cubic-bezier(0.4,0,0.2,1)',overflowY:'auto'}}>
        <div style={{padding:'20px 16px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <NavLink to="/dashboard" onClick={onClose} style={{display:'flex',alignItems:'center',gap:'10px',textDecoration:'none'}}>
            {theme.logoUrl
              ? <img src={theme.logoUrl} alt="logo" style={{width:'34px',height:'34px',borderRadius:'9px',objectFit:'contain',background:'rgba(255,255,255,0.06)',padding:'3px'}} onError={e=>{(e.currentTarget as HTMLImageElement).style.display='none';}} />
              : <div style={{width:'34px',height:'34px',borderRadius:'9px',background:`linear-gradient(135deg,${theme.primaryColor},${theme.accentColor})`,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'17px',color:'white'}}>{theme.logoText || 'L'}</div>
            }
            <div>
              <div style={{fontSize:'12px',fontWeight:900,color:'#3b82f6',letterSpacing:'2px',textTransform:'uppercase' as 'uppercase'}}>{theme.orgNameOverride || 'LEMAI'}</div>
              <div style={{fontSize:'9px',color:'#444',textTransform:'uppercase' as 'uppercase',letterSpacing:'1px'}}>
                {viewingOrg ? viewingOrg.org_name : ((user as any)?.org_name || 'Intelligence Platform')}
              </div>
            </div>
          </NavLink>
          <button onClick={onClose} style={{background:'rgba(255,255,255,0.06)',border:'none',borderRadius:'8px',color:'#555555',width:'32px',height:'32px',fontSize:'16px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
        </div>
        <SiteSwitcher />
        <nav style={{flex:1,padding:'8px 10px'}}>
          {NAV.map(section => {
            const isOperator = user?.role === 'operator' || user?.role === 'contractor';
            const visibleItems = section.items.filter(item => {
              if (isOperator && item.slug === 'admin/users') return false;
              if (isOperator && item.slug === 'settings') return false;
              return true;
            });
            if (visibleItems.length === 0) return null;
            return (
            <div key={section.label} style={{marginBottom:'20px'}}>
              <div style={{fontSize:'9px',fontWeight:700,color:'#444',letterSpacing:'1.5px',textTransform:'uppercase',margin:'8px 0 2px 12px'}}>{section.label}</div>
              {visibleItems.map(item => {
                const licensed = hasModule(item.slug);
                const topLevel = isTopLevelSlug(item.slug);
                if (!licensed) {
                  return (
                    <div key={item.slug} style={{display:'flex',alignItems:'center',gap:'10px',height:'32px',padding:'0 12px',borderRadius:0,marginBottom:'1px',opacity:0.32,cursor:'not-allowed',borderLeft:'2px solid transparent'}}>
                      <NavIcon slug={item.slug} size={15} color="#555555" />
                      <span style={{flex:1,fontSize:'10px',fontWeight:500,color:'#555555'}}>{item.name}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#555555" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </div>
                  );
                }
                if (item.external) {
                  return (
                    <a key={item.slug} href={`/${item.slug}`} target="_blank" rel="noopener noreferrer" onClick={onClose}
                      style={{display:'flex',alignItems:'center',gap:'10px',height:'32px',padding:'0 12px',borderRadius:0,cursor:'pointer',fontSize:'11px',fontWeight:500,marginBottom:'1px',textDecoration:'none',color:'#5a5a5a',borderLeft:'2px solid transparent'}}>
                      <NavIcon slug={item.slug} size={15} color="#555555" />
                      <span style={{flex:1}}>{item.name}</span>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                  );
                }
                return (
                  <NavLink key={item.slug} to={topLevel ? `/${item.slug}` : `/m/${item.slug}`} onClick={onClose}
                    className={({isActive}) => isActive ? 'lm-nav-item lm-nav-active' : 'lm-nav-item'}
                    style={({isActive})=>({display:'flex',alignItems:'center',gap:'10px',height:'32px',padding:'0 12px',borderRadius:0,cursor:'pointer',fontSize:'10px',fontWeight:isActive?700:500,marginBottom:'1px',textDecoration:'none',color:isActive?'#e5e5e5':undefined,background:isActive?'#1a1a1a':undefined,borderLeft:isActive?'2px solid #3b82f6':'2px solid transparent'})}>
                    {({isActive}) => <>
                      <NavIcon slug={item.slug} size={15} color={isActive ? '#e5e5e5' : '#5a5a5a'} />
                      <span style={{flex:1}}>{item.name}</span>
                      {item.slug === 'soc' && _socActiveCount > 0 && (
                        <span style={{background:'#dc2626',color:'#fff',borderRadius:0,fontSize:'9px',fontWeight:700,padding:'0 4px',minWidth:'16px',textAlign:'center',lineHeight:'16px',animation:'pulse 1.5s ease-in-out infinite'}}>
                          {_socActiveCount}
                        </span>
                      )}
                    </>}
                  </NavLink>
                );
              })}
            </div>
            );
          })}
        </nav>
        <div style={{borderTop:'1px solid rgba(255,255,255,0.06)',padding:'12px 10px'}}>
          {isSuperAdmin && (
            <NavLink to="/admin/orgs" onClick={onClose} style={({isActive})=>({display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',borderRadius:'8px',marginBottom:'4px',textDecoration:'none',color:isActive?'#f59e0b':'#555555',fontSize:'13px',fontWeight:500,background:isActive?'rgba(245,158,11,0.1)':'transparent'})}>
              <NavIcon slug="admin/orgs" size={15} color="#555555" />Manage Orgs
            </NavLink>
          )}
          <div style={{padding:'8px 12px',marginBottom:'4px'}}>
            <div style={{fontSize:'13px',fontWeight:500,color:'white'}}>{user?.first_name} {user?.last_name}</div>
            <div style={{fontSize:'11px',color:'#555555',marginTop:'2px'}}>{user?.email}</div>
            {isSuperAdmin && <div style={{fontSize:'10px',color:'#3b82f6',marginTop:'2px',fontWeight:600}}>Super Admin</div>}
            {!isSuperAdmin && user?.role === 'operator' && <div style={{fontSize:'10px',color:'#22c55e',marginTop:'2px',fontWeight:600}}>Operator</div>}
            {!isSuperAdmin && user?.role === 'contractor' && <div style={{fontSize:'10px',color:'#f59e0b',marginTop:'2px',fontWeight:600}}>Contractor</div>}
          </div>
          <div onClick={()=>{logout();navigate('/login');onClose();}} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',borderRadius:'8px',cursor:'pointer',color:'#555555',fontSize:'13px',fontWeight:500}}
            onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background='rgba(239,68,68,0.1)';(e.currentTarget as HTMLElement).style.color='#f87171';}}
            onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background='transparent';(e.currentTarget as HTMLElement).style.color='#555555';}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign Out
          </div>
        </div>
      </div>
    </>
  );
}

export default function Layout({ children, title, subtitle }: { children: React.ReactNode; title: string; subtitle?: string }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifs, setNotifs] = useState<Notif[]>(SEED_NOTIFS);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const unreadCount = notifs.filter(n => !n.read).length;
  const { alarms, wsStatus } = useAlarmSocket();
  const activeAlarms = alarms.filter(a => !a.acknowledged && !a.closedAt);
  const { activeSite } = useSite();
  const { toggleMode, isDark } = useTheme();
  const { log: auditLog } = useAudit();
  const { user } = useAuth();

  const [muted, setMuted] = useState(() => localStorage.getItem('lemai_mute_alarms') === '1');
  const seenAlarmIds = useRef<Set<string|number>>(new Set());

  function toggleMute() {
    setMuted(m => {
      const next = !m;
      localStorage.setItem('lemai_mute_alarms', next ? '1' : '0');
      return next;
    });
  }

  // Wire live alarms → notification bell
  useEffect(() => {
    const newNotifs: Notif[] = [];
    activeAlarms.forEach(a => {
      const aid = a.id ?? `${a.type}-${a.timestamp}`;
      if (!seenAlarmIds.current.has(aid)) {
        seenAlarmIds.current.add(aid);
        newNotifs.push({
          id: `alarm-${aid}`,
          type: 'alarm',
          title: `${a.type} — ${a.site || 'Unknown Site'}`,
          body: a.location || 'SOC response required',
          time: (a.timestamp || '').slice(11, 16) || new Date().toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit' }),
          read: false,
          link: '/m/soc',
        });
      }
    });
    if (newNotifs.length > 0) {
      setNotifs(prev => {
        const combined = [...newNotifs, ...prev];
        return combined.slice(0, 50); // cap at 50
      });
    }
  }, [activeAlarms]);

  // Update browser tab title with live alarm count
  useEffect(() => {
    document.title = activeAlarms.length > 0 ? `⚠ ${activeAlarms.length} | Lemai` : 'Lemai';
  }, [activeAlarms.length]);

  const markRead = useCallback((id: string) => {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  // Keyboard shortcut: / opens search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT','TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div style={{minHeight:'100vh',backgroundColor:'#0a0a0a',display:'flex',flexDirection:'column'}}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
        @keyframes critGlow{0%,100%{box-shadow:0 0 8px rgba(239,68,68,0.3),0 2px 12px rgba(0,0,0,0.4)}50%{box-shadow:0 0 24px rgba(239,68,68,0.6),0 2px 12px rgba(0,0,0,0.4)}}
        @keyframes highGlow{0%,100%{box-shadow:0 0 6px rgba(245,158,11,0.2),0 2px 12px rgba(0,0,0,0.4)}50%{box-shadow:0 0 18px rgba(245,158,11,0.45),0 2px 12px rgba(0,0,0,0.4)}}
        @keyframes slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes btnSuccess{0%{transform:scale(1)}30%{transform:scale(1.06)}100%{transform:scale(1)}}
        @media(min-width:768px){.sidebar-wrap div:last-child{transform:translateX(0)!important;} .main-content{margin-left:260px!important;} .menu-btn{display:none!important;}}
        @media(max-width:767px){
          .lm-hide-mobile{display:none!important;}
          /* Make all tables scroll horizontally on small screens */
          table{display:block;overflow-x:auto;-webkit-overflow-scrolling:touch;}
          /* Collapse common multi-column grid patterns to single column */
          .main-content [style*="grid-template-columns: repeat(3"]{grid-template-columns:1fr!important;}
          .main-content [style*="grid-template-columns: repeat(4"]{grid-template-columns:1fr!important;}
          .main-content [style*="grid-template-columns: repeat(auto-fill"]{grid-template-columns:1fr!important;}
          /* Reduce padding on mobile for content area */
          .main-content > div:last-child{padding:12px!important;}
          .main-content > div:nth-last-child(2){padding:12px 12px 0!important;}
          /* Make KPI stat rows stack */
          .lm-kpi-row{flex-wrap:wrap!important;}
          /* Min-width 0 for flex children to prevent overflow */
          .main-content [style*="display: flex"] > *{min-width:0;}
          /* Touch-friendly buttons */
          button,a[role="button"]{min-height:40px;}
        }
        /* Sidebar nav items */
        .lm-nav-item{color:#5a5a5a;}
        .lm-nav-item:hover:not(.lm-nav-active){background:#111111!important;color:#888888!important;}
        .lm-nav-item:hover:not(.lm-nav-active) svg{stroke:#888888!important;}
        /* Skeleton loader */
        .skeleton{background:linear-gradient(90deg,rgba(255,255,255,0.04) 25%,rgba(255,255,255,0.09) 50%,rgba(255,255,255,0.04) 75%);background-size:400px 100%;animation:shimmer 1.4s ease-in-out infinite;border-radius:6px;}
        /* Premium card depth */
        .lm-card{background:#0d0d0d;border:1px solid #1a1a1a;border-radius:0;}
        /* Tab content fade-in */
        .lm-tab-content{animation:slideUp 0.18s ease-out;}
        /* Button micro-feedback */
        .lm-btn-success{animation:btnSuccess 0.3s ease-out;}
        /* ── Light mode: comprehensive overrides ── */
        /* Text */
        [data-theme="light"] .main-content{color:#111111;}
        [data-theme="light"] .main-content h1,[data-theme="light"] .main-content h2,[data-theme="light"] .main-content h3{color:#0a0a0a!important;}
        [data-theme="light"] .main-content th{color:#111111!important;}
        [data-theme="light"] .main-content td{color:#222222;}
        [data-theme="light"] .main-content [style*="color: white"]{color:#0a0a0a!important;}
        [data-theme="light"] .main-content [style*="color: rgb(226, 232, 240)"],[data-theme="light"] .main-content [style*="color: #e2e8f0"]{color:#0a0a0a!important;}
        [data-theme="light"] .main-content [style*="color: rgb(241, 245, 249)"],[data-theme="light"] .main-content [style*="color: #f1f5f9"]{color:#0a0a0a!important;}
        [data-theme="light"] .main-content [style*="color: rgb(203, 213, 225)"],[data-theme="light"] .main-content [style*="color: #cbd5e1"]{color:#111111!important;}
        [data-theme="light"] .main-content [style*="color: rgb(147, 197, 253)"],[data-theme="light"] .main-content [style*="color: #93c5fd"]{color:#1d4ed8!important;}
        [data-theme="light"] .main-content [style*="color: rgb(148, 163, 184)"],[data-theme="light"] .main-content [style*="color: #94a3b8"]{color:#374151!important;}
        [data-theme="light"] .main-content [style*="color: rgb(100, 116, 139)"],[data-theme="light"] .main-content [style*="color: #555555"]{color:#555555!important;}
        [data-theme="light"] .main-content [style*="color: rgb(71, 85, 105)"],[data-theme="light"] .main-content [style*="color: #555555"]{color:#1f2937!important;}
        [data-theme="light"] .main-content [style*="color: rgb(51, 65, 85)"],[data-theme="light"] .main-content [style*="color: #222222"]{color:#374151!important;}
        [data-theme="light"] .main-content [style*="color: rgb(30, 41, 59)"],[data-theme="light"] .main-content [style*="color: #111111"]{color:#0a0a0a!important;}
        /* Card surfaces: near-transparent white → opaque white */
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.02)"]{background:rgba(255,255,255,0.78)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.03)"]{background:rgba(255,255,255,0.9)!important;box-shadow:0 1px 6px rgba(0,0,0,0.07)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.04)"]{background:rgba(255,255,255,0.93)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.05)"]{background:rgba(255,255,255,0.96)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.06)"]{background:rgba(0,0,0,0.04)!important;border-color:rgba(0,0,0,0.09)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.07)"]{background:rgba(0,0,0,0.04)!important;border-color:rgba(0,0,0,0.09)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.08)"]{background:rgba(0,0,0,0.05)!important;border-color:rgba(0,0,0,0.11)!important;}
        /* Dark overlay banners → near-transparent */
        [data-theme="light"] .main-content [style*="rgba(0, 0, 0, 0.25)"]{background:rgba(0,0,0,0.03)!important;}
        [data-theme="light"] .main-content [style*="rgba(0, 0, 0, 0.3)"]{background:rgba(0,0,0,0.04)!important;}
        [data-theme="light"] .main-content [style*="rgba(0, 0, 0, 0.45)"]{background:rgba(0,0,0,0.05)!important;}
        /* Dark hex popup/modal backgrounds → white */
        [data-theme="light"] .main-content [style*="rgb(13, 19, 33)"]{background:#ffffff!important;}
        [data-theme="light"] .main-content [style*="rgb(10, 15, 30)"]{background:#f8fafc!important;}
        [data-theme="light"] .main-content [style*="rgb(15, 23, 42)"]{background:#f8fafc!important;}
        /* Borders: white-alpha → black-alpha */
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.1)"]{border-color:rgba(0,0,0,0.13)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.12)"]{border-color:rgba(0,0,0,0.16)!important;}
        [data-theme="light"] .main-content [style*="rgba(255, 255, 255, 0.15)"]{border-color:rgba(0,0,0,0.18)!important;}
        /* Inputs */
        [data-theme="light"] .main-content input,[data-theme="light"] .main-content select,[data-theme="light"] .main-content textarea{color:#0a0a0a!important;background:rgba(255,255,255,0.95)!important;border-color:rgba(0,0,0,0.15)!important;}
        /* Buttons with dark semi-transparent backgrounds → light equivalents */
        [data-theme="light"] .main-content button[style*="rgba(255, 255, 255, 0.06)"]{background:rgba(0,0,0,0.06)!important;color:#374151!important;}
        [data-theme="light"] .main-content button[style*="rgba(255, 255, 255, 0.03)"]{background:rgba(0,0,0,0.04)!important;}
        [data-theme="light"] .main-content button[style*="rgba(255, 255, 255, 0.05)"]{background:rgba(0,0,0,0.05)!important;}
        [data-theme="light"] .main-content button[style*="transparent"]{color:#374151!important;}
        /* Table rows */
        [data-theme="light"] .main-content tr:hover{background:rgba(0,0,0,0.03)!important;}
        /* Skeleton and cards */
        [data-theme="light"] .skeleton{background:linear-gradient(90deg,rgba(0,0,0,0.05) 25%,rgba(0,0,0,0.09) 50%,rgba(0,0,0,0.05) 75%);background-size:400px 100%;}
        [data-theme="light"] .lm-card{background:rgba(255,255,255,0.9);border:1px solid rgba(0,0,0,0.08);box-shadow:0 2px 12px rgba(0,0,0,0.08),inset 0 1px 0 rgba(255,255,255,0.8);}
        /* Topbar in light mode */
        [data-theme="light"] .main-content > div:first-child{background:#ffffff!important;border-bottom-color:rgba(0,0,0,0.1)!important;}
      `}</style>
      <div className="sidebar-wrap"><Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)} /></div>
      {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}
      {showNotifs && <NotifPanel notifs={notifs} onRead={markRead} onClose={() => setShowNotifs(false)} />}
      <div className="main-content" style={{marginLeft:0,flex:1,display:'flex',flexDirection:'column',transition:'margin-left 0.28s',background:'#0a0a0a'}}>
        <OrgSwitcherBanner />
        {/* WS Reconnection Banner */}
        {wsStatus !== 'connected' && (
          <div style={{
            display:'flex', alignItems:'center', gap:'10px', padding:'8px 16px',
            background: wsStatus === 'connecting' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.1)',
            borderBottom: `1px solid ${wsStatus === 'connecting' ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.25)'}`,
            fontSize:'12px', zIndex:29,
          }}>
            <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: wsStatus === 'connecting' ? '#f59e0b' : '#ef4444', animation:'pulse 1.2s ease-in-out infinite', flexShrink:0 }} />
            <span style={{ color: wsStatus === 'connecting' ? '#fbbf24' : '#f87171', fontWeight:600 }}>
              {wsStatus === 'connecting' ? 'Reconnecting to live alarm feed…' : 'Live feed disconnected — alarm updates paused'}
            </span>
            <span style={{ color:'rgba(255,255,255,0.3)', flex:1, fontSize:'11px' }}>
              {wsStatus === 'connecting' ? 'Alarms will resume automatically' : 'Check your network connection'}
            </span>
            <NavLink to="/settings" style={{ fontSize:'11px', color:'#555555', textDecoration:'underline' }}>Check integrations</NavLink>
          </div>
        )}
        <div style={{height:'42px',backgroundColor:'#080808',borderBottom:'1px solid #1a1a1a',display:'flex',alignItems:'center',padding:'0 16px',gap:'12px',position:'sticky',top:0,zIndex:30}}>
          <button className="menu-btn" onClick={()=>setSidebarOpen(true)} style={{background:'rgba(255,255,255,0.06)',border:'none',borderRadius:'8px',color:'white',width:'36px',height:'36px',fontSize:'18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>☰</button>
          <div style={{display:'flex',alignItems:'center',gap:'10px',fontSize:'12px',overflowX:'auto',flex:1,whiteSpace:'nowrap'}}>
            {activeAlarms.length > 0
              ? <NavLink to="/m/soc" style={{color:'#ef4444',fontWeight:700,textDecoration:'none',display:'flex',alignItems:'center',gap:'4px',fontSize:'10px'}}><span style={{width:'7px',height:'7px',borderRadius:'50%',background:'#ef4444',display:'inline-block',animation:'pulse 1.2s ease-in-out infinite'}}/>🚨 {activeAlarms.length} Active Alarm{activeAlarms.length!==1?'s':''}</NavLink>
              : <span style={{color:'#22c55e',fontWeight:600}}>✓ No Active Alarms</span>
            }
            <span style={{color:'var(--lm-text4,#222222)'}}>|</span>
            {activeSite
              ? <span style={{color:'var(--lm-text3,#94a3b8)'}}>📍 {activeSite.name}</span>
              : <span style={{color:'var(--lm-text3,#94a3b8)'}}>🌐 All Sites</span>
            }
            <span style={{color:'var(--lm-text4,#222222)'}}>|</span>
            <span style={{color: wsStatus==='connected'?'#22c55e':wsStatus==='connecting'?'#f59e0b':'#ef4444', fontSize:'10px', fontWeight:600}}>
              {wsStatus==='connected'?'● WS Live':wsStatus==='connecting'?'◌ WS Connecting':'○ WS Offline'}
            </span>
          </div>
          <button onClick={toggleMute} style={{background: muted ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.06)',border:'none',borderRadius:'8px',color: muted ? '#f87171' : '#555555',width:'32px',height:'32px',fontSize:'16px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}} title={muted ? 'Alarm sounds muted — click to unmute' : 'Mute alarm sounds'}>{muted ? '🔇' : '🔊'}</button>

          <button onClick={() => setShowSearch(true)} style={{background:'rgba(255,255,255,0.06)',border:'none',borderRadius:'8px',color:'#555555',width:'32px',height:'32px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}} title="Global Search (/)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </button>
          <div style={{position:'relative',flexShrink:0}}>
            <button onClick={() => setShowNotifs(s => !s)} style={{background: showNotifs?'rgba(59,130,246,0.15)':'rgba(255,255,255,0.06)',border:'none',borderRadius:'8px',color: showNotifs?'#93c5fd':'#555555',width:'32px',height:'32px',fontSize:'16px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>🔔</button>
            {unreadCount > 0 && <div style={{position:'absolute',top:'-4px',right:'-4px',width:'16px',height:'16px',borderRadius:'50%',background:'#ef4444',fontSize:'9px',fontWeight:700,color:'white',display:'flex',alignItems:'center',justifyContent:'center'}}>{unreadCount}</div>}
          </div>
          <div style={{fontSize:'11px',color:'var(--lm-text3,#555555)',flexShrink:0}}>{new Date().toLocaleTimeString('en-AU',{timeZone:'Australia/Sydney',hour:'2-digit',minute:'2-digit'})} AEST</div>
        </div>
        <Breadcrumbs />
        <div style={{padding:'4px 20px 4px',display:'flex',alignItems:'center',gap:'10px',minHeight:'28px'}}>
          <span style={{fontSize:'13px',fontWeight:700,color:'#e5e5e5',letterSpacing:'0.3px'}}>{title}</span>
          {subtitle&&<span style={{color:'#444',fontSize:'10px'}}>{subtitle}</span>}
        </div>
        <div style={{flex:1,padding:'16px 20px 20px',overflowY:'auto'}}>{children}</div>
      </div>
    </div>
  );
}
